import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Monitor, Car, Heart, Users, Shield } from 'lucide-react';
import CTA from '../../components/CTA';

const OmaghHypnotherapy: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Hypnotherapy Omagh | Online Sessions for County Tyrone</title>
        <meta
          name="description"
          content="Professional hypnotherapy for Omagh and West Tyrone. Online sessions available - no travel required. Anxiety, stress, chronic pain, trauma-informed care. CNHC registered. Supporting Omagh's resilient community."
        />
        <meta name="keywords" content="hypnotherapy Omagh, hypnotherapist Omagh, online hypnotherapy Tyrone, anxiety treatment Omagh, stress management Omagh, chronic pain Omagh, trauma therapy Omagh, therapist Omagh" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/omagh-hypnotherapy/" />
        <meta property="og:title" content="Hypnotherapy Omagh | Online Sessions for County Tyrone" />
        <meta property="og:description" content="Professional hypnotherapy for Omagh and West Tyrone. Online sessions available - no travel required. Anxiety, stress, chronic pain. Supporting Omagh's resilient community." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Serving Omagh",
            "description": "CNHC registered hypnotherapy serving Omagh and County Tyrone via online sessions. Specializing in anxiety, stress, chronic pain management, and trauma-informed care. Supporting western communities with accessible online therapy.",
            "url": "https://www.sharonclare-hypnotherapy.com/omagh-hypnotherapy/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "areaServed": {
              "@type": "City",
              "name": "Omagh, County Tyrone",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": [
              {
                "@type": "Place",
                "name": "The Arzen Collective",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "The Arzen Collective, 13B Antrim Street",
                  "addressLocality": "Lisburn",
                  "addressRegion": "County Antrim",
                  "postalCode": "BT28",
                  "addressCountry": "GB"
                }
              },
              {
                "@type": "Place",
                "name": "Belfast Clinic",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                  "addressLocality": "Belfast",
                  "addressRegion": "County Down",
                  "postalCode": "BT7 2FZ",
                  "addressCountry": "GB"
                }
              }
            ],
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ],
            "medicalSpecialty": "Clinical Hypnotherapy",
            "serviceType": [
              "Anxiety Treatment",
              "Stress Management",
              "Chronic Pain Management",
              "Trauma-Informed Care",
              "Smoking Cessation"
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Professional Hypnotherapy for Omagh
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Online sessions available for Omagh and County Tyrone residents
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <Monitor className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700 font-semibold">
                  Online sessions available - no travel required
                </p>
              </div>
              <p className="text-gray-600">
                <span className="font-semibold">We strongly recommend online sessions</span> as the practical option for Omagh residents. Online hypnotherapy is just as effective while eliminating lengthy travel.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Hypnotherapy for Omagh and West Tyrone
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                While I don't have a physical clinic in Omagh, I'm proud to serve the County Tyrone community through secure online hypnotherapy sessions. As a CNHC registered hypnotherapist with 15 years of nursing experience at Belfast City Hospital, I understand that geography shouldn't be a barrier to accessing quality mental health support. Traveling long distances for regular in-person sessions simply isn't practical for most Omagh residents. That's why online sessions are the ideal solution.
              </p>
              <p>
                Online hypnotherapy via secure video call provides exactly the same professional, evidence-based treatment as face-to-face sessions, with the crucial advantage of eliminating travel time entirely. You can attend from the comfort of your own home in Omagh, at times that fit your schedule, without the burden of a 2.5-hour round trip. For western County Tyrone communities that have historically faced barriers accessing specialist services, online therapy represents a genuine solution to the rural mental health access gap that has existed for too long.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Serving Omagh's Community Section */}
      <section className="py-16 sm:py-20 bg-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Serving Omagh's Resilient Community
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Omagh is the county town for Tyrone and the commercial heart of western Northern Ireland, serving a wide rural catchment that extends to the Donegal border and beyond. As a market town with deep roots and a strong community spirit, Omagh has shown remarkable resilience and continues to move forward with strength and determination. I understand the importance of sensitive, trauma-informed care that respects the experiences of all community members while focusing on healing and growth.
              </p>
              <p>
                Whether you're dealing with everyday stresses like work pressure, financial worries, or relationship challenges, or facing more complex issues such as anxiety, chronic pain, or past trauma, my approach is always respectful, confidential, and tailored to your individual needs. I work collaboratively with local GPs and healthcare providers to ensure you receive comprehensive support, and I'm committed to providing accessible mental health care for the western community.
              </p>
              <p>
                For Omagh residents, the benefit of online sessions extends beyond convenience. Many people in western areas face long distances to access specialist services in Belfast or Derry, creating real barriers to consistent care. Online therapy removes these barriers entirely. You don't need to arrange childcare, take time off work, or factor in fuel costs and travel time. You can schedule sessions during your lunch break, in the evening after work, or on weekends - whatever fits your life best.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How I Can Support You
            </h2>
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-primary-50 rounded-xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Shield className="w-8 h-8 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Anxiety and Stress Management
                    </h3>
                    <p className="text-gray-700">
                      Whether you're experiencing general anxiety, social anxiety, panic attacks, or stress related to work, finances, or daily life, hypnotherapy can help you develop calm, control, and confidence. I use evidence-based techniques to retrain your nervous system's response to stressors.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-accent-50 rounded-xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Heart className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Chronic Pain Management
                    </h3>
                    <p className="text-gray-700">
                      Hypnotherapy is highly effective for managing chronic pain conditions including back pain, fibromyalgia, arthritis, migraines, and neuropathic pain. Research shows it can reduce pain intensity by 29% on average, offering relief alongside medical treatment.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-accent-50 rounded-xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Users className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Trauma-Informed Support
                    </h3>
                    <p className="text-gray-700">
                      For clients dealing with past difficult experiences, I provide sensitive, trauma-informed care that respects your pace and boundaries. For complex trauma, I work closely with your GP and specialist services to ensure comprehensive support. My nursing background includes extensive experience with trauma and medical settings.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-primary-50 rounded-xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <CheckCircle className="w-8 h-8 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Habit Breaking and Life Changes
                    </h3>
                    <p className="text-gray-700">
                      Whether you want to quit smoking, manage your weight, overcome unwanted habits, or build healthier patterns, hypnotherapy addresses the subconscious drivers behind behaviors. This makes lasting change easier than willpower alone.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <h3 className="text-2xl font-heading font-semibold text-gray-900 mt-8 mb-4">
                Border Community and Rural Support
              </h3>
              <p>
                As Omagh sits close to the Donegal border and serves a wide rural catchment, I understand the unique context of border communities. Many residents navigate cross-border work, shopping, and family connections, and some face challenges related to economic pressures, rural isolation, or accessing services. My approach is completely non-judgmental and respectful of all backgrounds and circumstances.
              </p>
              <p>
                Online therapy is particularly valuable for people in rural areas around Omagh who may be 10-20 miles from the town center itself. If traveling to Omagh requires significant time, and traveling from Omagh to a clinic adds another hour or more, that's an unrealistic burden for regular therapy. With online sessions, you can access professional support from anywhere in West Tyrone, the Sperrins, or the border areas - no matter how remote.
              </p>

              <h3 className="text-2xl font-heading font-semibold text-gray-900 mt-8 mb-4">
                Evidence-Based Treatment You Can Trust
              </h3>
              <p>
                I'm registered with the Complementary and Natural Healthcare Council (CNHC), the UK's government-backed regulator for complementary therapies. This registration requires 450+ hours of professional training, ongoing CPD, professional indemnity insurance, and adherence to strict ethical guidelines. My 15 years as a registered nurse at Belfast City Hospital provides additional medical context and understanding of complex health conditions.
              </p>
              <p>
                Hypnotherapy isn't magic or mind control - it's an evidence-based therapeutic technique that works with your brain's natural neuroplasticity. Research shows it's effective for anxiety (60% reduction in symptoms), chronic pain management (29% pain reduction), and habit change when the client is motivated. I combine hypnotherapy with cognitive behavioral techniques, solution-focused therapy, and trauma-informed approaches for comprehensive support.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Crisis Resources Section */}
      <section className="py-16 sm:py-20 bg-red-50 border-l-4 border-red-600">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">
              Crisis Support for Omagh Residents
            </h2>
            <p className="text-lg text-gray-700 mb-6">
              If you're experiencing a mental health crisis, self-harm thoughts, or suicidal ideation, please reach out for immediate support. Hypnotherapy is not appropriate for crisis situations - you need urgent professional help.
            </p>
            <div className="bg-white rounded-xl p-6 space-y-4">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Western Health and Social Care Trust Crisis Response Team</h3>
                <p className="text-gray-700 mb-1">24/7 mental health crisis support for West Tyrone</p>
                <a href="tel:02882835252" className="text-primary-600 font-semibold text-lg hover:text-primary-700">
                  028 8283 5252
                </a>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Lifeline NI</h3>
                <p className="text-gray-700 mb-1">24/7 crisis helpline for anyone in distress or despair</p>
                <a href="tel:08088088000" className="text-primary-600 font-semibold text-lg hover:text-primary-700">
                  0808 808 8000
                </a>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Samaritans</h3>
                <p className="text-gray-700 mb-1">24/7 emotional support for anyone struggling to cope</p>
                <a href="tel:116123" className="text-primary-600 font-semibold text-lg hover:text-primary-700">
                  116 123
                </a>
              </div>
              <div className="pt-4 border-t border-gray-200">
                <p className="text-gray-700">
                  <strong>In an emergency:</strong> Call 999 or go to the emergency department at South West Acute Hospital, Omagh.
                </p>
                <p className="text-gray-700 mt-2">
                  <strong>GP referral:</strong> For complex mental health needs including diagnosed conditions (depression, PTSD, bipolar disorder, schizophrenia, psychosis), please speak to your GP. Hypnotherapy can support mental wellness but is not a replacement for psychiatric care.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Attending Sessions from Omagh
            </h2>

            <div className="bg-accent-100 border-l-4 border-accent-600 rounded-r-xl p-6 mb-8">
              <div className="flex items-start space-x-4">
                <Monitor className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                    Online Sessions: The Practical Choice
                  </h3>
                  <p className="text-gray-700 mb-4">
                    For Omagh clients, we <strong>strongly recommend online sessions</strong> rather than traveling to our physical clinics. Here's why:
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Distance:</strong> Approximately 70 miles to the Belfast clinic (around 1 hr 20 min) - that's nearly 3 hours of travel for a 60-minute session</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Cost:</strong> 100+ mile round trip means the cost of fuel, plus wear on your vehicle</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Time:</strong> 3+ hours total commitment (travel + session) vs 60 minutes at home</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Convenience:</strong> No childcare arrangements, no time off work, sessions fit around your life</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Consistency:</strong> Easier to commit to regular sessions when travel isn't a barrier</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Equally effective:</strong> Research shows online hypnotherapy produces the same outcomes as in-person sessions</span>
                    </li>
                  </ul>
                  <p className="text-gray-700 mt-4">
                    Online sessions use secure, GDPR-compliant video calling (Zoom or similar). You'll need a private space, stable internet connection, and a device with a camera (laptop, tablet, or smartphone). Sessions are conducted exactly as in-person appointments, with the same professionalism, privacy, and therapeutic effectiveness.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-100 rounded-xl p-6">
              <div className="flex items-start space-x-4">
                <Car className="w-8 h-8 text-gray-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                    In-Person Sessions (Available but Not Recommended)
                  </h3>
                  <p className="text-gray-700 mb-4">
                    If you prefer in-person sessions despite the distance, two clinic locations are available: the Belfast clinic on the Ormeau Road and the Lisburn clinic on Antrim Street, both with days and times that vary week to week. However, online sessions are strongly recommended for Omagh residents due to the travel involved.
                  </p>
                  <div className="space-y-3 text-gray-700">
                    <div>
                      <p className="font-semibold">To Belfast Clinic (Belfast Wellness Clinic, 228 Ormeau Road, BT7 2FZ):</p>
                      <p>From Omagh: approximately 70 miles via the A5 south, then the M1 east into Belfast and south along the Ormeau Road (A24). Around 1 hour 20 minutes. The clinic is in south Belfast, roughly 10 minutes from the city centre. By public transport, Botanic and City Hospital are the closest rail stations, with Lanyon Place serving regional routes, and Metro buses run along the Ormeau Road corridor. Clinic days and times vary - current availability and online booking are on the{' '}
                        <a
                          href="https://belfastchiropracticclinic.janeapp.co.uk/#/staff_member/37"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-accent-600 hover:text-accent-700 font-medium underline"
                        >
                          clinic booking page
                        </a>
                        , and early evening appointments are available.</p>
                    </div>
                    <div>
                      <p className="font-semibold">To Lisburn Clinic (The Arzen Collective, 13B Antrim Street, BT28):</p>
                      <p>From Omagh: the A5 south, then the M1 east, leaving at the Lisburn junctions — a shorter run than continuing into Belfast. Clinic days and times vary, and early evening appointments are available.</p>
                    </div>
                  </div>
                  <p className="text-gray-700 mt-4 italic">
                    Honest advice: For weekly or fortnightly sessions over several weeks, the travel commitment from Omagh isn't sustainable for most people. Online sessions offer the same quality of care with none of the travel burden.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Sessions and Packages
            </h2>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-gray-50 rounded-xl p-6 text-center">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">Initial Consultation</h3>
                <p className="text-gray-600">90 minutes including assessment and first session</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-6 text-center">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">Single Session</h3>
                <p className="text-gray-600">60-minute follow-up session</p>
              </div>
              <div className="bg-accent-100 rounded-xl p-6 text-center border-2 border-accent-600">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">4-Session Package</h3>
                <p className="text-gray-600">Best value for ongoing work, at a discount on single sessions</p>
              </div>
            </div>
            <p className="text-center text-gray-600 mb-4">
              Online appointments carry no travel costs.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Common Questions from Omagh Clients
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Are online sessions as effective as in-person?
                </h3>
                <p className="text-gray-700">
                  Yes. Research shows online hypnotherapy produces the same therapeutic outcomes as face-to-face sessions. The hypnotic trance state is just as deep, the therapeutic techniques are identical, and the relationship between therapist and client develops equally well. Many clients actually find it easier to relax at home in their own familiar environment. The only requirement is a private space, stable internet, and a device with a camera.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Do you travel to Omagh to see clients?
                </h3>
                <p className="text-gray-700">
                  I don't currently offer mobile services to Omagh due to the 100+ mile round trip from my clinics. However, online sessions eliminate the need for either of us to travel, making professional hypnotherapy much more accessible and affordable for West Tyrone residents. This also means I can offer more flexible appointment times without being limited by travel schedules.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Do you work with people from rural areas and border communities?
                </h3>
                <p className="text-gray-700">
                  Absolutely. I work with clients across Northern Ireland, including rural areas around Omagh, West Tyrone, the Sperrins, and border communities. My approach is completely non-judgmental and respectful of all backgrounds, experiences, and circumstances. Online therapy is particularly valuable for rural clients who may face long distances to access specialist services.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with anxiety and stress?
                </h3>
                <p className="text-gray-700">
                  Yes, anxiety and stress are among the most common and successfully treated issues with hypnotherapy. Research shows hypnotherapy can reduce anxiety symptoms by up to 60% through retraining your nervous system's stress response. Whether you're dealing with general anxiety, social anxiety, panic attacks, work stress, financial worries, or health anxiety, hypnotherapy can help you develop lasting calm and control.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Do you provide trauma-informed care?
                </h3>
                <p className="text-gray-700">
                  Yes. I provide sensitive, trauma-informed care that respects your pace and boundaries. My 15 years of nursing experience at Belfast City Hospital included working with trauma patients, and I understand the importance of psychological safety in therapy. For complex trauma or diagnosed PTSD, I work collaboratively with your GP and specialist services to ensure you receive comprehensive support. Hypnotherapy can be a valuable part of trauma recovery but should complement, not replace, specialist care.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Is hypnotherapy safe? Will I lose control?
                </h3>
                <p className="text-gray-700">
                  Hypnotherapy is completely safe and you remain in control throughout. Hypnosis is a natural state of focused attention - similar to being absorbed in a book or film - not unconsciousness or sleep. You can't be made to do anything against your will, you'll remember everything, and you can open your eyes and end the session at any time. It's a collaborative process where you're an active participant in your own healing.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  How many sessions will I need?
                </h3>
                <p className="text-gray-700">
                  This varies depending on your goals and circumstances. Some clients achieve their goals in 3-4 sessions (habit change, specific phobias), while others benefit from 6-8 sessions for more complex issues (chronic anxiety, pain management, trauma). We'll discuss realistic expectations in your initial consultation and review progress regularly. There's no pressure to commit to multiple sessions - you can decide after each appointment whether to continue.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Do you bulk bill or accept health insurance?
                </h3>
                <p className="text-gray-700">
                  Hypnotherapy is a private service and not covered by the NHS. However, some private health insurance policies (Benenden Health, Westfield Health, Vitality Health) include complementary therapy benefits. Check your policy details. Payment is required at the time of booking. I provide receipts that you can submit to your insurer for potential reimbursement.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Can I see you if I'm taking medication for anxiety or depression?
                </h3>
                <p className="text-gray-700">
                  Yes. Hypnotherapy works alongside medication (SSRIs, SNRIs, benzodiazepines) without any negative interactions. In fact, research shows that combining hypnotherapy with medication and/or talking therapy produces better outcomes than any single treatment alone. Never stop or change medication without consulting your GP. I can work collaboratively with your doctor to provide integrated care.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  How do I book an appointment?
                </h3>
                <p className="text-gray-700">
                  You can book online via my Calendly link (click "Book Free Consultation" above) or call me directly on 07932 139245. The initial consultation is 90 minutes and includes a detailed assessment, discussion of your goals, explanation of how hypnotherapy works, and your first session. There's no obligation to continue after the initial consultation - you can decide if it's right for you.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              What County Tyrone Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-accent-50 rounded-xl p-6">
                <p className="text-gray-700 italic mb-4">
                  "I was skeptical about online therapy but Sharon made it so easy and comfortable. I didn't have to drive over an hour each way which made it much easier to commit to regular sessions. My anxiety has improved dramatically and I'm sleeping better than I have in years."
                </p>
                <p className="text-gray-900 font-semibold">- M.K., County Tyrone</p>
              </div>

              <div className="bg-primary-50 rounded-xl p-6">
                <p className="text-gray-700 italic mb-4">
                  "Sharon's nursing background really came through in how she understood my chronic pain condition. She worked with my GP and the approach was always professional and evidence-based. Online sessions fitted around my work schedule perfectly."
                </p>
                <p className="text-gray-900 font-semibold">- T.L., West Tyrone</p>
              </div>

              <div className="bg-accent-50 rounded-xl p-6">
                <p className="text-gray-700 italic mb-4">
                  "I appreciated Sharon's sensitive, non-judgmental approach. She understood the context of living in a rural border community and never made assumptions. The hypnotherapy helped me manage stress and anxiety that had been building for years."
                </p>
                <p className="text-gray-900 font-semibold">- P.D., County Tyrone</p>
              </div>

              <div className="bg-primary-50 rounded-xl p-6">
                <p className="text-gray-700 italic mb-4">
                  "After years of smoking I finally managed to quit with Sharon's help. Three sessions and I haven't had a cigarette in 8 months. The online format meant I could fit appointments around my farm work. Best money I ever spent."
                </p>
                <p className="text-gray-900 font-semibold">- J.M., West Tyrone</p>
              </div>

              <div className="bg-accent-50 rounded-xl p-6">
                <p className="text-gray-700 italic mb-4">
                  "I was dealing with anxiety and panic attacks that were affecting my daily life. Sharon's calm, professional approach and the hypnotherapy techniques gave me tools I still use every day. I feel like myself again. Highly recommend."
                </p>
                <p className="text-gray-900 font-semibold">- L.R., County Tyrone</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <CTA />
    </main>
  );
};

export default OmaghHypnotherapy;
